import React from 'react';

import * as Yup from 'yup';

import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { ConfirmationCodeInput } from '@common/react/components/Forms/ConfirmationCodeInput/ConfirmationCodeInput';
import { RequestButton } from '@common/react/components/Core/LoadingButton/LoadingButton';
import { phoneRegexp, simpleStringValidator } from '@common/react/utils/validationHelpers';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { BaseInit } from '@common/react/objects/BaseInit';
import { AuthResult } from '@common/react/objects/LoginFormValues';
import Button from '@common/react/components/Forms/Button';
import { WithId } from '@common/typescript/objects/WithId';
import Alert from '@common/react/components/antd/Alert/Alert';

interface Props<User extends BaseUser, TInit extends BaseInit<User>> {
	phone: string;
	afterSubmit?: ({ phone }: { phone: string }, res: AuthResult<User, TInit>) => void;
	authRequest: string;
	sendCodeRequest?: string;
	resendCodeRequest?: string;
	openModal?: boolean;
}

interface SendCodeFormValues extends WithId {
	login: string;
	loginCode: string;
	path: string;
}

const validationSchemaConfirm = Yup.object().shape({
	loginCode: simpleStringValidator,
});

const SendCodeButton: <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: Props<User, TInit>,
) => React.ReactElement<User> = <User extends BaseUser, TInit extends BaseInit<User> = BaseInit<User>>(
	props: Props<User, TInit>,
) => {
	const {
		phone,
		afterSubmit,
		authRequest = 'authByCode',
		sendCodeRequest = 'sendLoginCode',
		openModal = false,
		resendCodeRequest = sendCodeRequest,
	} = props;
	const { renderModal, openErrorMessage } = useModal();
	const [open, setOpen] = React.useState(openModal);
	const isPhoneValid = React.useMemo(() => {
		return phoneRegexp.test(phone || '');
	}, [phone]);

	const [time, setTime] = React.useState(30);
	const ref = React.useRef<any>(null);

	const startTimer = () => {
		ref.current = null;
		setTime(60);
		ref.current = setInterval(() => {
			setTime((prev) => {
				const newTime = prev - 1;
				if (!newTime && ref.current) {
					clearInterval(ref.current);
					ref.current = null;
				}
				return newTime;
			});
		}, 1000);
		return () => {
			if (ref.current) {
				clearInterval(ref.current);
			}
		};
	};

	React.useEffect(() => {
		startTimer();
	}, []);

	React.useEffect(() => {
		if (openModal && !open) {
			setOpen(openModal);
		}
	}, [openModal]);

	if (!isPhoneValid) return <></>;

	return <>
		<RequestButton
			requestType={sendCodeRequest}
			requestProps={{ phoneNumber: phone }}
			className="btn btn-sm btn-primary send-code-btn"
			onSuccess={() => {
				setOpen(true);
			}}
		>
			Send Code
		</RequestButton>
		{renderModal(open, () => setOpen(false), {
			title: 'Login Code',
			destroyOnHidden: true,
			width: 500,
			footer: null,
			children: <>
				<div className="clearfix">
					<Alert
						className="pull-left"
						style={{ marginBottom: 10 }}
						title={<div style={{ display: 'inline-block', marginLeft: 5 }}>
							Security code has been sent to your phone number.
						</div>}
					/>
				</div>
				<div>
					Didn't receive a code?
					{' '}
					{time ? `Resend code in ${time} seconds.`
						: (
							<RequestButton
								className="resend-code-btn btn btn-default"
								beforeAction={() => {
									startTimer();
									return true;
								}}
								onError={(e) => {
									openErrorMessage?.(e);

									if (ref.current) {
										clearInterval(ref.current);
										ref.current = null;
										setTime(0);
										startTimer();
									}
								}}
								requestType={resendCodeRequest}
								requestProps={{ phoneNumber: phone }}
								successMessage="Security code has been successfully sent."
							>
								Resend code
							</RequestButton>)}
				</div>
				<ItemProvider<SendCodeFormValues, AuthResult<User, TInit>>
					id={-1}
					add={{
						loginCode: '',
						login: phone,
						path: '/',
					}}
					saveRequest={authRequest}
					validationSchema={validationSchemaConfirm}
					resetAfterSave
				>
					<ItemEditor<SendCodeFormValues, AuthResult<User, TInit>>
						successMessage=""
						afterSubmit={({ res }) => {
							setOpen(false);
							afterSubmit && afterSubmit({ phone }, res);
						}}
						saveText="Send"
						edit={({ formikBag, state: { loading } }) => <div className="row">
							<FormikField
								containerClassName="col-sm-12 form-group mt10"
								fieldName="loginCode"
								title=""
								render={(fieldProps) =>
									<div className="text-center">
										<label htmlFor="loginCode">Code</label>
										<ConfirmationCodeInput
											fieldProps={{
												...fieldProps,
												field: {
													...fieldProps.field,
													onChange: (e) => {
														const value = e.currentTarget.value;
														fieldProps.field.onChange(e);
														if (value.length === 4 && !loading) {
															formikBag.submitForm();
														}
													},
												},
											}}
										/>
									</div>
								}
							/>
							<div className="text-center form-group">
								<Button isLoading={loading} disabled={formikBag.values.loginCode.length !== 4}>Send</Button>
							</div>
						</div>}
					/>
				</ItemProvider>
			</>,
		})}
	</>;
};

export default SendCodeButton;
